// components/Sidebar.js
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  Home,
  Calendar,
  Users,
  BookOpen,
  Cloud,
  Image as ImageIcon,
  Box,
  Package,
  Settings as SettingsIcon,
  DollarSign,
  Bike,
  Key,
  Wallet,
  Sparkles,
  FileSpreadsheet,
  MessageSquare,
  FilePlus,
  List,
} from 'lucide-react';

export default function Sidebar({ isSidebarOpen, setSidebarOpen, showMobileSidebar, setShowMobileSidebar }) {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({
    shops:
      location.pathname.startsWith('/shops') ||
      location.pathname.startsWith('/shop-categories') ||
      location.pathname.startsWith('/shop-products') ||
      location.pathname.startsWith('/shop-menu-sections') ||
      location.pathname.startsWith('/shop-logins'),
    catalog:
      location.pathname.startsWith('/product-categories') ||
      location.pathname.startsWith('/product-brands') ||
      location.pathname.startsWith('/availability-rules') ||
      location.pathname.startsWith('/products'),
    orders: location.pathname.startsWith('/orders'),
    settings:
      location.pathname.startsWith('/delivery-charges') ||
      location.pathname.startsWith('/quick-order-suggestions'),
    settlements:
      location.pathname.startsWith('/delivery-settlements') ||
      location.pathname.startsWith('/shop-settlements'),
    deliveryExecutives:
      location.pathname.startsWith('/delivery-executives') ||
      location.pathname.startsWith('/delivery-executive-logins'),
    expenses:
      location.pathname.startsWith('/expenses') ||
      location.pathname.startsWith('/expense-types'),
  });

  const toggleMenu = (menu) => {
    setOpenMenus((prev) => ({ ...prev, [menu]: !prev[menu] }));
  };

  const NavItem = ({ to, icon: Icon, label }) => (
    <Link
      to={to}
      onClick={() => setShowMobileSidebar(false)}
      className={`flex items-center space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${location.pathname === to
          ? 'bg-white text-indigo-600 font-semibold'
          : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
        }`}
    >
      <Icon size={20} />
      {isSidebarOpen && <span>{label}</span>}
    </Link>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`transition-all duration-300 bg-[#F7F7FA] text-gray-700 dark:bg-gray-800 dark:text-gray-200 shadow-md z-40 flex-col ${isSidebarOpen ? 'w-64' : 'w-16'
          } hidden md:flex`}
      >
        <div className="p-4 mt-1 flex items-center justify-between">
          {isSidebarOpen ? (
            <span className="text-indigo-600 dark:text-white font-bold text-xl">ATA Online Services</span>
          ) : (
            <span className="text-indigo-600 dark:text-white font-bold text-xl hidden">S</span>
          )}
          <button
            onClick={() => setSidebarOpen(!isSidebarOpen)}
            className="text-gray-600 dark:text-gray-300 md:inline"
          >
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        <nav className="flex flex-col space-y-1 p-2">
          <NavItem to="/dashboard" icon={Home} label="Dashboard" />
          <div>
            <button
              onClick={() => toggleMenu('shops')}
              className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                location.pathname.startsWith('/shops') ||
                location.pathname.startsWith('/shop-categories') ||
                location.pathname.startsWith('/shop-products') ||
                location.pathname.startsWith('/shop-menu-sections') ||
                location.pathname.startsWith('/shop-logins')
                  ? 'bg-white text-indigo-600 font-semibold'
                  : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Calendar size={20} />
              {isSidebarOpen && <span>Shops</span>}
            </button>
              {openMenus.shops && (
              <div className="pl-4 mt-1">
                <NavItem to="/shops" icon={Users} label="Shop List" />
                <NavItem to="/shop-categories" icon={BookOpen} label="Shop Categories" />
                <NavItem to="/shop-products" icon={Package} label="Shop Products" />
                <NavItem to="/shop-menu-sections" icon={List} label="Menus" />
                <NavItem to="/shop-logins" icon={Key} label="Shop Logins" />
              </div>
            )}
          </div>
          <NavItem to="/customers" icon={Users} label="Customers" />
          <NavItem to="/contact-enquiries" icon={MessageSquare} label="Contact Enquiries" />
          <div>
            <button
              onClick={() => toggleMenu('deliveryExecutives')}
              className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                location.pathname.startsWith('/delivery-executives') ||
                location.pathname.startsWith('/delivery-executive-logins')
                  ? 'bg-white text-indigo-600 font-semibold'
                  : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Bike size={20} />
              {isSidebarOpen && <span>Delivery Executives</span>}
            </button>
            {openMenus.deliveryExecutives && (
              <div className="pl-4 mt-1">
                <NavItem to="/delivery-executives" icon={Users} label="List" />
                <NavItem to="/delivery-executive-logins" icon={Key} label="App Logins" />
              </div>
            )}
          </div>
          <div>
            <button
              onClick={() => toggleMenu('catalog')}
              className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                location.pathname.startsWith('/product-categories') ||
                location.pathname.startsWith('/product-brands') ||
                location.pathname.startsWith('/availability-rules') ||
                location.pathname.startsWith('/products')
                  ? 'bg-white text-indigo-600 font-semibold'
                  : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Box size={20} />
              {isSidebarOpen && <span>Product Catalog</span>}
            </button>
            {openMenus.catalog && (
              <div className="pl-4 mt-1">
                <NavItem to="/product-categories" icon={BookOpen} label="Categories" />
                <NavItem to="/product-brands" icon={ImageIcon} label="Brands" />
                <NavItem to="/availability-rules" icon={Cloud} label="Availability Rules" />
                <NavItem to="/products" icon={Package} label="Products" />
              </div>
            )}
          </div>
          <div>
            <button
              onClick={() => toggleMenu('orders')}
              className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                location.pathname.startsWith('/orders')
                  ? 'bg-white text-indigo-600 font-semibold'
                  : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Package size={20} />
              {isSidebarOpen && <span>Orders</span>}
            </button>
            {openMenus.orders && (
            <div className="pl-4 mt-1">
              <NavItem to="/orders" icon={Package} label="Order List" />
              <NavItem to="/orders/create" icon={Package} label="Create Order" />
              <NavItem to="/orders/reports/status" icon={FileSpreadsheet} label="Order Status Report" />
              <NavItem to="/orders/reports/shops" icon={FileSpreadsheet} label="Shop Order Report" />
            </div>
          )}
        </div>
          <div>
            <button
              onClick={() => toggleMenu('settings')}
              className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
              location.pathname.startsWith('/delivery-charges')
                ? 'bg-white text-indigo-600 font-semibold'
                : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
            }`}
            >
              <SettingsIcon size={20} />
              {isSidebarOpen && <span>Settings</span>}
            </button>
            {openMenus.settings && (
              <div className="pl-4 mt-1">
                <NavItem to="/delivery-charges" icon={DollarSign} label="Delivery Charges" />
                <NavItem to="/quick-order-suggestions" icon={Sparkles} label="Quick Suggestion List" />
              </div>
            )}
          </div>
          <div>
            <button
              onClick={() => toggleMenu('expenses')}
              className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                location.pathname.startsWith('/expenses') ||
                location.pathname.startsWith('/expense-types')
                  ? 'bg-white text-indigo-600 font-semibold'
                  : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <DollarSign size={20} />
              {isSidebarOpen && <span>Expenses</span>}
            </button>
            {openMenus.expenses && (
              <div className="pl-4 mt-1">
                <NavItem to="/expenses" icon={FileSpreadsheet} label="Expense List" />
                <NavItem to="/expense-types" icon={BookOpen} label="Expense Types" />
              </div>
            )}
          </div>
          <div>
            <button
              onClick={() => toggleMenu('settlements')}
              className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                location.pathname.startsWith('/delivery-settlements') ||
                location.pathname.startsWith('/shop-settlements')
                  ? 'bg-white text-indigo-600 font-semibold'
                  : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Wallet size={20} />
              {isSidebarOpen && <span>Settlements</span>}
            </button>
            {openMenus.settlements && (
              <div className="pl-4 mt-1 space-y-1">
                <NavItem to="/delivery-settlements" icon={Wallet} label="Delivery Settlements" />
                <NavItem to="/shop-settlements/generate" icon={FilePlus} label="Generate Shop Settlement" />
                <NavItem to="/shop-settlements" icon={Wallet} label="Shop Settlements" />
              </div>
            )}
          </div>
      </nav>
    </aside>

      {/* Mobile Sidebar */}
      {showMobileSidebar && (
        <div className="fixed inset-0 z-40 bg-[#F7F7FA] text-gray-700 dark:bg-gray-800 dark:text-gray-200 shadow-md flex flex-col w-64 md:hidden">
          <div className="p-4 flex items-center justify-between">
            <span className="text-indigo-600 dark:text-white font-bold text-xl">ATA Online Services</span>
            <button onClick={() => setShowMobileSidebar(false)} className="text-gray-600 dark:text-gray-300">
              <X size={20} />
            </button>
          </div>
          <nav className="flex flex-col space-y-1 p-2">
            <NavItem to="/dashboard" icon={Home} label="Dashboard" />
            <div>
              <button
                onClick={() => toggleMenu('shops')}
              className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                  location.pathname.startsWith('/shops') ||
                  location.pathname.startsWith('/shop-categories') ||
                  location.pathname.startsWith('/shop-products') ||
                  location.pathname.startsWith('/shop-menu-sections') ||
                  location.pathname.startsWith('/shop-logins')
                    ? 'bg-white text-indigo-600 font-semibold'
                    : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Calendar size={20} />
                {isSidebarOpen && <span>Shops</span>}
              </button>
            {openMenus.shops && (
              <div className="pl-4 mt-1">
                <NavItem to="/shops" icon={Users} label="Shop List" />
                <NavItem to="/shop-categories" icon={BookOpen} label="Shop Categories" />
                <NavItem to="/shop-products" icon={Package} label="Shop Products" />
                <NavItem to="/shop-menu-sections" icon={List} label="Menus" />
                <NavItem to="/shop-logins" icon={Key} label="Shop Logins" />
              </div>
            )}
          </div>
          <NavItem to="/customers" icon={Users} label="Customers" />
          <NavItem to="/contact-enquiries" icon={MessageSquare} label="Contact Enquiries" />
          <div>
            <button
              onClick={() => toggleMenu('deliveryExecutives')}
              className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                location.pathname.startsWith('/delivery-executives') ||
                location.pathname.startsWith('/delivery-executive-logins')
                  ? 'bg-white text-indigo-600 font-semibold'
                  : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Bike size={20} />
              {isSidebarOpen && <span>Delivery Executives</span>}
            </button>
            {openMenus.deliveryExecutives && (
              <div className="pl-4 mt-1">
                <NavItem to="/delivery-executives" icon={Users} label="List" />
                <NavItem to="/delivery-executive-logins" icon={Key} label="App Logins" />
              </div>
            )}
          </div>
          <div>
            <button
              onClick={() => toggleMenu('catalog')}
              className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                location.pathname.startsWith('/product-categories') ||
                  location.pathname.startsWith('/product-brands') ||
                  location.pathname.startsWith('/availability-rules') ||
                  location.pathname.startsWith('/products')
                    ? 'bg-white text-indigo-600 font-semibold'
                    : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Box size={20} />
                {isSidebarOpen && <span>Product Catalog</span>}
              </button>
              {openMenus.catalog && (
                <div className="pl-4 mt-1">
                  <NavItem to="/product-categories" icon={BookOpen} label="Categories" />
                  <NavItem to="/product-brands" icon={ImageIcon} label="Brands" />
                  <NavItem to="/availability-rules" icon={Cloud} label="Availability Rules" />
                  <NavItem to="/products" icon={Package} label="Products" />
                </div>
              )}
            </div>
            <div>
              <button
                onClick={() => toggleMenu('orders')}
                className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                  location.pathname.startsWith('/orders')
                    ? 'bg-white text-indigo-600 font-semibold'
                    : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Package size={20} />
                {isSidebarOpen && <span>Orders</span>}
              </button>
              {openMenus.orders && (
                <div className="pl-4 mt-1">
                  <NavItem to="/orders" icon={Package} label="Order List" />
                  <NavItem to="/orders/create" icon={Package} label="Create Order" />
                  <NavItem to="/orders/reports/status" icon={FileSpreadsheet} label="Order Status Report" />
                  <NavItem to="/orders/reports/shops" icon={FileSpreadsheet} label="Shop Order Report" />
                </div>
              )}
            </div>
            <div>
              <button
                onClick={() => toggleMenu('settings')}
                className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                  location.pathname.startsWith('/delivery-charges')
                    ? 'bg-white text-indigo-600 font-semibold'
                    : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <SettingsIcon size={20} />
                {isSidebarOpen && <span>Settings</span>}
              </button>
              {openMenus.settings && (
                <div className="pl-4 mt-1">
                  <NavItem to="/delivery-charges" icon={DollarSign} label="Delivery Charges" />
                  <NavItem to="/quick-order-suggestions" icon={Sparkles} label="Quick Suggestion List" />
                </div>
              )}
            </div>
            <div>
              <button
                onClick={() => toggleMenu('expenses')}
                className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                  location.pathname.startsWith('/expenses') ||
                  location.pathname.startsWith('/expense-types')
                    ? 'bg-white text-indigo-600 font-semibold'
                    : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <DollarSign size={20} />
                {isSidebarOpen && <span>Expenses</span>}
              </button>
              {openMenus.expenses && (
                <div className="pl-4 mt-1">
                  <NavItem to="/expenses" icon={FileSpreadsheet} label="Expense List" />
                  <NavItem to="/expense-types" icon={BookOpen} label="Expense Types" />
                </div>
              )}
            </div>
            <div>
              <button
                onClick={() => toggleMenu('settlements')}
                className={`flex items-center w-full space-x-3 px-4 py-2 rounded-md text-sm transition-colors ${
                  location.pathname.startsWith('/delivery-settlements') ||
                  location.pathname.startsWith('/shop-settlements')
                    ? 'bg-white text-indigo-600 font-semibold'
                    : 'text-gray-600 hover:bg-white hover:text-indigo-600 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Wallet size={20} />
                {isSidebarOpen && <span>Settlements</span>}
              </button>
              {openMenus.settlements && (
                <div className="pl-4 mt-1 space-y-1">
                  <NavItem to="/delivery-settlements" icon={Wallet} label="Delivery Settlements" />
                  <NavItem to="/shop-settlements/generate" icon={FilePlus} label="Generate Shop Settlement" />
                  <NavItem to="/shop-settlements" icon={Wallet} label="Shop Settlements" />
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
